import html2canvas from 'html2canvas';

/**
 * Download image from URL with proper filename
 */
export const downloadImage = async (imageUrl, filename = 'image.jpg') => {
  try {
    const response = await fetch(imageUrl);
    if (!response.ok) throw new Error('Failed to fetch image');

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error('Image download failed:', error);
    throw error;
  }
};

/**
 * Load an image with CORS support to avoid tainted canvas
 */
const loadImageWithCORS = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => {
      // If CORS fails, try without it as fallback
      const imgFallback = new Image();
      imgFallback.onload = () => resolve(imgFallback);
      imgFallback.onerror = reject;
      imgFallback.src = src;
    };
    img.src = src;
  });
};

/**
 * Capture and download the canvas element as an image
 * Creates a properly sized canvas with person image at original size and background filling all space
 */
export const downloadCanvasAsImage = async (canvasElement, filename = 'canvas-image.jpg') => {
  try {
    if (!canvasElement) {
      throw new Error('Canvas element not found');
    }

    console.log('📸 Capturing canvas for download...');

    // Find the person image element to get its natural dimensions
    const personImgElement = canvasElement.querySelector('.person-image-layer img');
    const backgroundElement = canvasElement.querySelector('.main-canvas');

    if (!personImgElement) {
      console.warn('⚠️ No person image found, capturing canvas with background only');
      return await standardCanvasCapture(canvasElement, filename, backgroundElement);
    }

    // Load person image with CORS to avoid tainted canvas
    const personImg = await loadImageWithCORS(personImgElement.src);

    // Get the natural (original) dimensions of the person image
    const naturalWidth = personImg.naturalWidth;
    const naturalHeight = personImg.naturalHeight;

    console.log('📐 Person image natural dimensions:', { naturalWidth, naturalHeight });

    // Create a new canvas with the person image's natural dimensions
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = naturalWidth;
    outputCanvas.height = naturalHeight;
    const ctx = outputCanvas.getContext('2d', { willReadFrequently: true });

    // Get background color or image
    const backgroundStyle = window.getComputedStyle(backgroundElement);
    const backgroundColor = backgroundStyle.backgroundColor;
    const backgroundImage = backgroundStyle.backgroundImage;

    console.log('🎨 Background detection:', { backgroundColor, backgroundImage });

    // Always start with white background for PNG transparency support
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, naturalWidth, naturalHeight);

    // Fill background
    if (backgroundImage && backgroundImage !== 'none') {
      // Check if it's a gradient (linear-gradient, radial-gradient, etc.)
      if (backgroundImage.includes('gradient')) {
        console.log('🌈 Rendering gradient background');
        // For gradients, we need to create a temporary canvas with the gradient
        // because we can't load gradients like images
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = naturalWidth;
        tempCanvas.height = naturalHeight;
        const tempCtx = tempCanvas.getContext('2d');

        // Create a gradient on the temp canvas
        // We'll use a div to render the gradient and capture it
        const tempDiv = document.createElement('div');
        tempDiv.style.width = `${naturalWidth}px`;
        tempDiv.style.height = `${naturalHeight}px`;
        tempDiv.style.background = backgroundImage;
        tempDiv.style.position = 'absolute';
        tempDiv.style.left = '-9999px';
        document.body.appendChild(tempDiv);

        try {
          // Use html2canvas to capture the gradient
          const html2canvas = (await import('html2canvas')).default;
          const gradientCanvas = await html2canvas(tempDiv, {
            backgroundColor: null,
            width: naturalWidth,
            height: naturalHeight,
            scale: 1
          });

          // Draw the gradient canvas onto our output canvas
          ctx.drawImage(gradientCanvas, 0, 0, naturalWidth, naturalHeight);
        } catch (err) {
          console.warn('⚠️ Failed to render gradient, using color fallback');
          ctx.fillStyle = backgroundColor || '#FFFFFF';
          ctx.fillRect(0, 0, naturalWidth, naturalHeight);
        } finally {
          document.body.removeChild(tempDiv);
        }
      } else {
        // It's a regular image URL
        const urlMatch = backgroundImage.match(/url\(['"]?([^'"]+)['"]?\)/);
        if (urlMatch) {
          try {
            const bgImg = await loadImageWithCORS(urlMatch[1]);
            // Draw background image to fill entire canvas (on top of white background)
            ctx.drawImage(bgImg, 0, 0, naturalWidth, naturalHeight);
          } catch (err) {
            console.warn('⚠️ Failed to load background image, using color fallback');
            ctx.fillStyle = backgroundColor || '#FFFFFF';
            ctx.fillRect(0, 0, naturalWidth, naturalHeight);
          }
        }
      }
    } else if (backgroundColor && backgroundColor !== 'transparent' && backgroundColor !== 'rgba(0, 0, 0, 0)') {
      // Fill with solid background color (on top of white background)
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, naturalWidth, naturalHeight);
    } else {
      // Default background color
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, naturalWidth, naturalHeight);
    }

    // Draw the person image at its natural size, centered
    ctx.drawImage(personImg, 0, 0, naturalWidth, naturalHeight);

    // Now draw any clothing items on top
    const clothingItemElements = canvasElement.querySelectorAll('.dropped-items-layer img');
    const personImgRect = personImgElement.getBoundingClientRect();

    // Calculate scale factor from displayed size to natural size
    const scaleX = naturalWidth / personImgRect.width;
    const scaleY = naturalHeight / personImgRect.height;

    console.log('📐 Scale factors:', { scaleX, scaleY, itemCount: clothingItemElements.length });

    for (const itemElement of clothingItemElements) {
      if (!itemElement.complete) continue;

      try {
        // Load clothing item with CORS
        const itemImg = await loadImageWithCORS(itemElement.src);

        const itemRect = itemElement.getBoundingClientRect();

        // Calculate position relative to person image
        const relativeX = (itemRect.left - personImgRect.left) * scaleX;
        const relativeY = (itemRect.top - personImgRect.top) * scaleY;
        const scaledWidth = itemRect.width * scaleX;
        const scaledHeight = itemRect.height * scaleY;

        // Get rotation if any
        const transform = window.getComputedStyle(itemElement.parentElement).transform;
        let rotation = 0;
        if (transform && transform !== 'none') {
          const values = transform.split('(')[1].split(')')[0].split(',');
          const a = parseFloat(values[0]);
          const b = parseFloat(values[1]);
          rotation = Math.atan2(b, a);
        }

        // Draw the clothing item
        ctx.save();
        ctx.translate(relativeX + scaledWidth / 2, relativeY + scaledHeight / 2);
        ctx.rotate(rotation);
        ctx.drawImage(itemImg, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
        ctx.restore();
      } catch (err) {
        console.warn('⚠️ Failed to load clothing item:', err);
        // Continue with other items
      }
    }

    console.log('📸 Composite canvas created:', {
      width: outputCanvas.width,
      height: outputCanvas.height
    });

    // Convert canvas to blob and download
    return new Promise((resolve, reject) => {
      outputCanvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob from canvas'));
          return;
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        console.log('✅ Canvas downloaded successfully');
        resolve(true);
      }, 'image/jpeg', 0.95);
    });
  } catch (error) {
    console.error('Canvas download failed:', error);
    throw error;
  }
};

/**
 * Create a blob from the canvas element using natural dimensions (for mobile sharing)
 * This preserves the original aspect ratio of the person image
 */
export const createCanvasBlob = async (canvasElement) => {
  try {
    if (!canvasElement) {
      throw new Error('Canvas element not found');
    }

    console.log('📸 Creating canvas blob with natural dimensions...');

    // Find the person image element to get its natural dimensions
    const personImgElement = canvasElement.querySelector('.person-image-layer img');
    const backgroundElement = canvasElement.querySelector('.main-canvas');

    if (!personImgElement) {
      console.warn('⚠️ No person image found, using html2canvas fallback');
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(canvasElement, {
        backgroundColor: '#FFFFFF',
        useCORS: true,
        allowTaint: true,
        scale: 2
      });
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) reject(new Error('Failed to create blob'));
          else resolve(blob);
        }, 'image/jpeg', 0.95);
      });
    }

    // Load person image with CORS to avoid tainted canvas
    const personImg = await loadImageWithCORS(personImgElement.src);

    // Get the natural (original) dimensions of the person image
    const naturalWidth = personImg.naturalWidth;
    const naturalHeight = personImg.naturalHeight;

    console.log('📐 Person image natural dimensions for blob:', { naturalWidth, naturalHeight });

    // Create a new canvas with the person image's natural dimensions
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = naturalWidth;
    outputCanvas.height = naturalHeight;
    const ctx = outputCanvas.getContext('2d', { willReadFrequently: true });

    // Get background color or image
    const backgroundStyle = backgroundElement ? window.getComputedStyle(backgroundElement) : null;
    const backgroundColor = backgroundStyle?.backgroundColor;
    const backgroundImage = backgroundStyle?.backgroundImage;

    // Always start with white background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, naturalWidth, naturalHeight);

    // Fill background
    if (backgroundImage && backgroundImage !== 'none') {
      if (backgroundImage.includes('gradient')) {
        // For gradients, create a temp div and capture it
        const tempDiv = document.createElement('div');
        tempDiv.style.width = `${naturalWidth}px`;
        tempDiv.style.height = `${naturalHeight}px`;
        tempDiv.style.background = backgroundImage;
        tempDiv.style.position = 'absolute';
        tempDiv.style.left = '-9999px';
        document.body.appendChild(tempDiv);

        try {
          const html2canvas = (await import('html2canvas')).default;
          const gradientCanvas = await html2canvas(tempDiv, {
            backgroundColor: null,
            width: naturalWidth,
            height: naturalHeight,
            scale: 1
          });
          ctx.drawImage(gradientCanvas, 0, 0, naturalWidth, naturalHeight);
        } catch {
          ctx.fillStyle = backgroundColor || '#FFFFFF';
          ctx.fillRect(0, 0, naturalWidth, naturalHeight);
        } finally {
          document.body.removeChild(tempDiv);
        }
      } else {
        const urlMatch = backgroundImage.match(/url\(['"]?([^'"]+)['"]?\)/);
        if (urlMatch) {
          try {
            const bgImg = await loadImageWithCORS(urlMatch[1]);
            ctx.drawImage(bgImg, 0, 0, naturalWidth, naturalHeight);
          } catch {
            ctx.fillStyle = backgroundColor || '#FFFFFF';
            ctx.fillRect(0, 0, naturalWidth, naturalHeight);
          }
        }
      }
    } else if (backgroundColor && backgroundColor !== 'transparent' && backgroundColor !== 'rgba(0, 0, 0, 0)') {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, naturalWidth, naturalHeight);
    } else {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, naturalWidth, naturalHeight);
    }

    // Draw the person image at its natural size
    ctx.drawImage(personImg, 0, 0, naturalWidth, naturalHeight);

    // Draw clothing items on top
    const clothingItemElements = canvasElement.querySelectorAll('.dropped-items-layer img');
    const personImgRect = personImgElement.getBoundingClientRect();
    const scaleX = naturalWidth / personImgRect.width;
    const scaleY = naturalHeight / personImgRect.height;

    for (const itemElement of clothingItemElements) {
      if (!itemElement.complete) continue;

      try {
        const itemImg = await loadImageWithCORS(itemElement.src);
        const itemRect = itemElement.getBoundingClientRect();

        const relativeX = (itemRect.left - personImgRect.left) * scaleX;
        const relativeY = (itemRect.top - personImgRect.top) * scaleY;
        const scaledWidth = itemRect.width * scaleX;
        const scaledHeight = itemRect.height * scaleY;

        const transform = window.getComputedStyle(itemElement.parentElement).transform;
        let rotation = 0;
        if (transform && transform !== 'none') {
          const values = transform.split('(')[1].split(')')[0].split(',');
          const a = parseFloat(values[0]);
          const b = parseFloat(values[1]);
          rotation = Math.atan2(b, a);
        }

        ctx.save();
        ctx.translate(relativeX + scaledWidth / 2, relativeY + scaledHeight / 2);
        ctx.rotate(rotation);
        ctx.drawImage(itemImg, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
        ctx.restore();
      } catch {
        // Continue with other items
      }
    }

    console.log('📸 Composite canvas blob created:', {
      width: outputCanvas.width,
      height: outputCanvas.height
    });

    // Return blob
    return new Promise((resolve, reject) => {
      outputCanvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob from canvas'));
          return;
        }
        resolve(blob);
      }, 'image/jpeg', 0.95);
    });
  } catch (error) {
    console.error('Canvas blob creation failed:', error);
    throw error;
  }
};

/**
 * Fallback standard canvas capture using html2canvas
 */
const standardCanvasCapture = async (canvasElement, filename, backgroundElement = null) => {
  const rect = canvasElement.getBoundingClientRect();

  // Get background color or image from backgroundElement if provided
  let backgroundColor = '#FFFFFF'; // Default to white

  if (backgroundElement) {
    const backgroundStyle = window.getComputedStyle(backgroundElement);
    const bgColor = backgroundStyle.backgroundColor;
    const bgImage = backgroundStyle.backgroundImage;

    console.log('🎨 Standard capture - Background detection:', { bgColor, bgImage });

    // Check if it's a gradient
    if (bgImage && bgImage !== 'none' && bgImage.includes('gradient')) {
      console.log('🌈 Standard capture - Gradient detected, html2canvas will render it');
      // For gradients, use white as base - html2canvas will capture the gradient
      backgroundColor = '#FFFFFF';
    }
    // If there's a background color, use it
    else if (bgColor && bgColor !== 'transparent' && bgColor !== 'rgba(0, 0, 0, 0)') {
      backgroundColor = bgColor;
    } else if (bgImage && bgImage !== 'none') {
      // For background images, use white as base
      backgroundColor = '#FFFFFF';
    }
  }

  const canvas = await html2canvas(canvasElement, {
    backgroundColor: backgroundColor,
    useCORS: true,
    allowTaint: true,
    scale: 2,
    logging: false,
    width: Math.floor(rect.width),
    height: Math.floor(rect.height)
  });

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to create blob from canvas'));
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      resolve(true);
    }, 'image/jpeg', 0.95);
  });
};

export default downloadImage;